import { useState, useEffect } from 'react';
import { Settings, Cookie, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';

export const CookieSettings = () => {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true
    analytics: false,
    advertising: false
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Load current preferences
    const consent = localStorage.getItem('cookie_consent');
    const consentPrefs = localStorage.getItem('cookie_preferences');
    
    if (consentPrefs) {
      try {
        setPreferences(JSON.parse(consentPrefs));
      } catch (e) {
        // If old format, derive from consent value
        if (consent === 'accepted') {
          setPreferences({
            necessary: true,
            analytics: true,
            advertising: true
          });
        }
      }
    } else if (consent === 'accepted') {
      setPreferences({
        necessary: true,
        analytics: true,
        advertising: true
      });
    }
  }, []);

  const handleSave = () => {
    // Save preferences
    localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    
    // Update consent status
    if (preferences.analytics || preferences.advertising) {
      localStorage.setItem('cookie_consent', 'accepted');
    } else {
      localStorage.setItem('cookie_consent', 'partial');
    }

    // Update Google Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.advertising ? 'granted' : 'denied',
        'ad_user_data': preferences.advertising ? 'granted' : 'denied',
        'ad_personalization': preferences.advertising ? 'granted' : 'denied'
      });
    }

    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      navigate(-1); // Go back to previous page
    }, 2000);
  };

  const handleAcceptAll = () => {
    setPreferences({
      necessary: true,
      analytics: true,
      advertising: true
    });
    localStorage.setItem('cookie_preferences', JSON.stringify({
      necessary: true,
      analytics: true,
      advertising: true
    }));
    localStorage.setItem('cookie_consent', 'accepted');
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }

    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      navigate(-1);
    }, 2000);
  };

  const handleRejectAll = () => {
    setPreferences({
      necessary: true,
      analytics: false,
      advertising: false
    });
    localStorage.setItem('cookie_preferences', JSON.stringify({
      necessary: true,
      analytics: false,
      advertising: false
    }));
    localStorage.setItem('cookie_consent', 'declined');
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }

    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      navigate(-1);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Cookie Settings - Crescent Electrical Ltd"
        description="Manage your cookie preferences for the Crescent Electrical Ltd website."
        canonical="https://crescentelectrical.co.uk/cookie-settings"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2563EB] to-blue-700 text-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="h-10 w-10" />
            <h1 className="text-4xl sm:text-5xl font-bold">Cookie Settings</h1>
          </div>
          <p className="text-xl text-blue-100">
            Manage your cookie preferences
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          
          {/* Success Message */}
          {saved && (
            <Card className="bg-green-50 border-green-200 mb-6">
              <CardContent className="pt-6 flex items-center gap-3">
                <Check className="h-6 w-6 text-green-600" />
                <p className="text-green-800 font-semibold">Your cookie preferences have been saved!</p>
              </CardContent>
            </Card>
          )}

          {/* Cookie Categories */}
          <div className="space-y-6 mb-8">
            
            {/* Necessary */}
            <Card className="border-gray-300">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cookie className="h-6 w-6 text-gray-900" />
                    <span>Necessary Cookies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={true}
                      disabled={true}
                      className="opacity-50"
                    />
                    <span className="text-sm text-gray-500">Always Active</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-sm">
                  These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of necessary cookies.
                </p>
              </CardContent>
            </Card>

            {/* Analytics */}
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cookie className="h-6 w-6 text-[#2563EB]" />
                    <span>Analytics Cookies</span>
                  </div>
                  <Switch 
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, analytics: checked }))}
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-sm mb-3">
                  Analytics cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the website experience.
                </p>
                <p className="text-xs text-gray-600">
                  <strong>Provider:</strong> Google Analytics 4
                </p>
              </CardContent>
            </Card>

            {/* Advertising */}
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cookie className="h-6 w-6 text-[#F97316]" />
                    <span>Advertising/Marketing Cookies</span>
                  </div>
                  <Switch 
                    checked={preferences.advertising}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, advertising: checked }))}
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-sm mb-3">
                  Advertising cookies track your online activity to help advertisers deliver more relevant advertising and to measure the performance of advertising campaigns. We use Google Ads conversion tracking.
                </p>
                <p className="text-xs text-gray-600">
                  <strong>Provider:</strong> Google Ads
                </p>
              </CardContent>
            </Card>

          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={handleAcceptAll}
              className="flex-1 bg-[#2563EB] hover:bg-blue-700 text-white"
              size="lg"
            >
              Accept All
            </Button>
            <Button 
              onClick={handleSave}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              Save My Preferences
            </Button>
            <Button 
              onClick={handleRejectAll}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              Reject Non-Essential
            </Button>
          </div>

          <p className="text-sm text-gray-600 text-center mt-6">
            Learn more in our <a href="/cookies" className="text-[#2563EB] hover:underline">Cookie Policy</a>
          </p>
        </div>
      </section>
    </div>
  );
};
